import { Power0, TweenMax } from 'gsap';
import { Container, Sprite } from 'pixi.js';
import { Animation } from '../../Animations/Animation';
import { Star } from '../../Animations/Star';
import DEG_TO_RAD = PIXI.DEG_TO_RAD;

export class StarsMeter extends Container {
    public starPositionsSet: boolean = false;
    private barBg: Sprite;
    private starBarAnim: Sprite;
    private barMask: PIXI.Graphics;
    private stars: Sprite[];
    private readonly starsFx: Star[];
    private readonly starsArea: number;
    private maxScore: number;
    private tiers: number[];
    private timeline: Animation;

    constructor(isPortrait: boolean) {
        super();

        const bg = new Sprite(PIXI.utils.TextureCache['gameui/top-hud-starsbar-bg']);
        const fill = new Sprite(PIXI.utils.TextureCache['gameui/top-hud-starsbar-fill']);

        const starBarAnim = new Sprite(PIXI.utils.TextureCache['gameui/tophud-starsbar-fill-glow']);

        const stars = [];
        this.starsFx = [];
        while (stars.length < 3) {
            const c = new Sprite();
            const m = new Sprite(PIXI.utils.TextureCache['gameui/top-hud-starsbar-marker']);
            m.anchor.set(0.5);
            const s = new Sprite(PIXI.utils.TextureCache['gameui/top-hud-starsbar-slot']);
            s.anchor.set(0.5);
            const star = new Sprite(PIXI.utils.TextureCache['commonui/top-hud-starsbar-star']);
            star.anchor.set(0.5);
            star.alpha = 0;
            star.y = s.height / 2;
            m.x = s.width / 4;
            m.y = s.height;
            star.scale.set(0.7);
            const fxs = new Star(0.5, false);
            this.starsFx.push(fxs);
            const fxsSprite = fxs.sprite;
            s.x += s.width * 0.27;
            s.y += s.height * 0.2;
            star.position = fxsSprite.position = s.position;

            c.addChild(s, star, m, fxsSprite);
            c.x = ((bg.width * 0.95) / 3) * stars.length + 1;
            s.rotation = star.rotation = fxs.sprite.rotation = isPortrait ? 0 : 90 * DEG_TO_RAD;
            stars.push(c);
        }

        bg.y = stars[0].y + (stars[0].getChildAt(0) as Sprite).height;
        starBarAnim.anchor.set(0, 0.1);
        starBarAnim.y = stars[0].y + (stars[0].getChildAt(0) as Sprite).height;
        starBarAnim.alpha = 0;
        fill.y = bg.y - fill.height * 0.14;

        const fillMask = new PIXI.Graphics();
        fillMask.beginFill(0x000000);
        fillMask.drawRect(0.001, fill.y, fill.width, fill.height);
        fillMask.cacheAsBitmap = true;
        fill.mask = fillMask;
        this.starsArea = bg.width * 0.95;
        this.barBg = bg;
        this.starBarAnim = starBarAnim;
        this.barMask = fillMask;
        this.barMask.scale.set(0.001, 1);
        this.stars = stars;
        //@ts-ignore
        this.addChild(bg, fill, ...stars, fillMask, starBarAnim);
    }

    public destroy() {
        super.destroy();
        this.barMask.cacheAsBitmap = false;
        this.starsFx.forEach(star => star.destroy());
    }

    public showStarPercents(tiers: number[], maxScore: number) {
        this.maxScore = maxScore;
        this.tiers = tiers;
        this.stars.forEach((s, i) => {
            s.x = (this.starsArea * tiers[i]) / maxScore;
        });
        this.starPositionsSet = true;
    }

    public showScoreProgress(score: number, timedLevel?: boolean) {
        const scorePercent = Math.max(0.001, score / this.maxScore);

        if (timedLevel) {
            if (this.timeline) {
                this.timeline.kill();
                this.timeline.destroy();
            }
            const timeLine = (this.timeline = new Animation({ pauseWithGame: true }));
            timeLine.add(
                TweenMax.fromTo(
                    this.barMask.scale,
                    score,
                    { x: scorePercent },
                    { x: 0, ease: Power0.easeNone }
                )
            );
            this.tiers.forEach((tier, i) => {
                if (i === 0 || tier > score) {
                    return;
                }
                const time = score - (tier - Number.EPSILON);
                timeLine.add(
                    () => {
                        this.updateStars(score - timeLine.time);
                    },
                    `${time}`,
                    true
                );
            });
        } else {
            TweenMax.to(this.barMask.scale, 0.2, { x: scorePercent, ease: Power0.easeNone });
        }
        this.updateStars(score);
    }

    public startTime() {
        this.timeline.play();
    }

    public cleanUp() {
        TweenMax.killTweensOf(this.barMask.scale);
        this.starBarAnim.destroy();
        this.barMask.destroy();
        this.barBg.destroy();
        this.stars.forEach(s => s.destroy());
        this.stars = [];
        this.destroy();
    }

    private updateStars(score: number) {
        this.stars.forEach((star, index) => {
            const currentAlpha = star.getChildAt(1).alpha;
            const targetAlpha = score >= this.tiers[index] ? 1 : 0;
            star.getChildAt(1).alpha = targetAlpha;
            if (currentAlpha !== 1 && targetAlpha === 1) {
                this.playStarAnimation(this.starsFx[index]);
            }
        });
    }

    private playStarAnimation(starAn: Star) {
        const star = starAn;
        star.play();
        TweenMax.to(this.starBarAnim, 0.25, {
            alpha: 1,
            onComplete: () => {
                TweenMax.to(this.starBarAnim, 0.25, {
                    alpha: 0,
                });
            },
        });
    }
}
